import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import "./FeaturedArtworks.scss";
import { logo } from "../../../../assets";
import { toTitleCase } from "../../../../services/data_requests";

const SLIDE_INTERVAL = 10000;
const GATEWAY =
  process.env.REACT_APP_PINATA_GATEWAY || "https://gateway.pinata.cloud";

const resolveIPFS = (uri) => {
  if (!uri) return "";
  return uri.startsWith("ipfs://")
    ? uri.replace("ipfs://", `${GATEWAY}/ipfs/`)
    : uri;
};

const fetchSubgraphItems = async (limit = 5) => {
  const GRAPH_URL = process.env.REACT_APP_GRAPH_URL;
  if (!GRAPH_URL) return [];

  const query = `{
    listings(
      where: { active: true }
      first: ${limit}
      orderBy: id
      orderDirection: desc
    ) {
      id
      pricePerToken
      quantity
      active
      token {
        id
        tokenId
        metadataUri
        collection {
          id
          name
          description
        }
      }
    }
  }`;

  try {
    const res = await fetch(GRAPH_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ query }),
    });
    const { data } = await res.json();
    const listings = data?.listings || [];

    const items = await Promise.all(
      listings.map(async (listing) => {
        let meta = {};
        if (listing.token?.metadataUri) {
          try {
            const uri = resolveIPFS(listing.token.metadataUri);
            const r = await fetch(uri);
            meta = await r.json();
          } catch {}
        }

        const price = (Number(listing.pricePerToken) / 1e18).toFixed(4);
        const imgSrc = resolveIPFS(meta.preview || meta.image || "");

        return {
          name: meta.name || listing.token?.collection?.name || "Unknown",
          description:
            meta.description || listing.token?.collection?.description || "",
          prev_img_url: imgSrc,
          price,
          contract_address: listing.token?.collection?.id || "",
          token_id: listing.token?.tokenId || "",
          artist: meta.artist || null,
        };
      }),
    );

    return items.filter((i) => i.prev_img_url);
  } catch (err) {
    console.error("FeaturedArtworks subgraph fetch error:", err);
    return [];
  }
};

const FeaturedArtworks = ({
  apiURL,
  data = [],
  useAPI = true,
  useSubgraph = false,
  buttonText = "Explore Artwork",
  manuallyFormatLink = (item) => "#",
  titleFormatter = (item) => item.name || "Untitled",
  subtitleFormatter = (item) =>
    item.artist?.name || item.artist || "Unknown Artist",
  descriptionFormatter = (item) => item.description || "",
  defaultImage = logo,
}) => {
  const FADE_MS = 220;

  const [items, setItems] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isFading, setIsFading] = useState(false);
  const [thumbsVisible, setThumbsVisible] = useState(false);
  const intervalRef = useRef(null);
  const fadeTimeoutRef = useRef(null);

  useEffect(() => {
    if (useSubgraph) {
      fetchSubgraphItems(5).then(setItems);
      return;
    }
    if (!useAPI) {
      setItems(data);
      return;
    }
    const fetchData = async () => {
      try {
        const res = await axios.get(apiURL);
        const newData = Array.isArray(res.data)
          ? res.data
          : res.data.items || [];
        setItems(newData);
      } catch (err) {
        console.error("FeaturedArtworks fetch error:", err);
      }
    };
    fetchData();
  }, [apiURL, useAPI, useSubgraph]);

  useEffect(() => {
    items.forEach((item) => {
      if (item?.prev_img_url) {
        const img = new Image();
        img.src = item.prev_img_url;
      }
    });
  }, [items]);

  const changeSlide = (indexOrUpdater) => {
    setIsFading(true);
    clearTimeout(fadeTimeoutRef.current);
    fadeTimeoutRef.current = setTimeout(() => {
      setActiveIndex((prev) =>
        typeof indexOrUpdater === "function"
          ? indexOrUpdater(prev)
          : indexOrUpdater,
      );
      setIsFading(false);
    }, FADE_MS);
  };

  useEffect(() => {
    if (!items.length) return;
    intervalRef.current = setInterval(() => {
      changeSlide((prev) => (prev + 1) % items.length);
    }, SLIDE_INTERVAL);
    return () => clearInterval(intervalRef.current);
  }, [items.length]);

  useEffect(() => {
    return () => clearTimeout(fadeTimeoutRef.current);
  }, []);

  const goTo = (indexOrUpdater) => {
    changeSlide(indexOrUpdater);
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      changeSlide((prev) => (prev + 1) % items.length);
    }, SLIDE_INTERVAL);
  };

  if (!items.length) return null;

  const current = items[activeIndex];
  const imgSrc = current?.prev_img_url || defaultImage;
  const title = titleFormatter(current);
  const description = descriptionFormatter(current);
  const displayPrice = current?.price || current?.price_usdt || null;

  return (
    <section className="fa-section">
      <div className="fa-header">
        <div>
          <h2 className="fa-heading">Featured Artworks</h2>
          <p className="fa-subheading">
            Experience art that tells stories beyond the canvas.
          </p>
        </div>
      </div>

      <div className="fa-body">
        <div
          className={`fa-image-wrap fa-crossfade ${isFading ? "fa-crossfade--out" : ""}`}
        >
          <img
            src={imgSrc}
            alt={title}
            className="fa-main-image"
            loading="lazy"
          />

          <div
            className={`fa-thumbs ${thumbsVisible ? "fa-thumbs--open" : ""}`}
          >
            {items.map((item, i) => (
              <button
                key={i}
                className={`fa-thumb ${i === activeIndex ? "fa-thumb--active" : ""}`}
                onClick={() => goTo(i)}
                aria-label={`Go to slide ${i + 1}`}
              >
                <img
                  src={item.prev_img_url || defaultImage}
                  alt={titleFormatter(item)}
                  loading="lazy"
                />
              </button>
            ))}
          </div>

          <button
            className="fa-thumb-toggle"
            onClick={() => setThumbsVisible((v) => !v)}
            aria-label="Toggle thumbnails"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <rect x="3" y="3" width="7" height="7" rx="1" />
              <rect x="14" y="3" width="7" height="7" rx="1" />
              <rect x="3" y="14" width="7" height="7" rx="1" />
              <rect x="14" y="14" width="7" height="7" rx="1" />
            </svg>
          </button>
        </div>

        <div
          className={`fa-info fa-crossfade ${isFading ? "fa-crossfade--out" : ""}`}
        >
          <div className="fa-info-inner">
            <div>
              <h3 className="fa-title">{toTitleCase(title)}</h3>
              <p className="fa-artist">
                by {toTitleCase(subtitleFormatter(current))}
              </p>
            </div>

            <div>
              <p className="fa-description">
                {description
                  ? description.slice(0, 320) +
                    (description.length > 320 ? "…" : "")
                  : "No description available."}
              </p>
            </div>
          </div>

          <div className="fa-info-bottom">
            <p className="fa-price">
              {displayPrice ? (
                <>
                  Price: <span>{displayPrice} USD</span>
                </>
              ) : (
                <>
                  Price: <span>—</span>
                </>
              )}
            </p>

            <Link to={manuallyFormatLink(current)} className="fa-explore-btn">
              {buttonText}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturedArtworks;
